// Background reminder manager for FUTURE payments (תשלום עתידי).
//
// Polls the productions list every 60s and, for each scheduled payment
// whose `scheduled_at` (or `snooze_until`) has passed and which hasn't
// been paid yet or dismissed, surfaces a modal:
//   "יש לך לגבות תשלום מ-<עסק> על סך <סכום>"
// with 3 actions:
//   1. מעבר לגביה  — scroll to that payment and close.
//   2. הזכר לי מאוחר יותר — snooze 4 hours.
//   3. הזכר לי מחר — snooze 24 hours.
// When the user later clicks "אישור" on the payment row (which sets
// paid=true + reminder_dismissed=true) the reminder stops.
//
// This component renders nothing when there's no active reminder.

import { useEffect, useMemo, useState } from "react";
import { Calendar, Bell, ArrowRight, AlarmClock, Clock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { updatePayment, listProductions } from "./api";

const HOUR = 60 * 60 * 1000;

const ils0 = (n) => new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(Number(n) || 0);

// Pick the FIRST due reminder across all productions (one popup at a
// time keeps the UX calm). A payment is "due" when:
//   - it has timing="future" and a scheduled_at timestamp
//   - it is NOT yet paid
//   - it has NOT been dismissed (reminder_dismissed != true)
//   - now >= max(scheduled_at, snooze_until)
function pickDueReminder(productions, snoozeMap) {
  const now = Date.now();
  for (const prod of productions || []) {
    for (const p of (prod.payments || [])) {
      if (p.timing !== "future" || !p.scheduled_at) continue;
      if (p.paid || p.reminder_dismissed) continue;
      const scheduled = new Date(p.scheduled_at).getTime();
      if (Number.isNaN(scheduled)) continue;
      // Server-side snooze (persisted) wins; client-side snooze (this
      // tab only) acts as a soft secondary so we don't re-prompt the
      // same person right after they snoozed during this session.
      const serverSnooze = p.snooze_until ? new Date(p.snooze_until).getTime() : 0;
      const clientSnooze = snoozeMap[p.id] || 0;
      const dueAt = Math.max(scheduled, serverSnooze, clientSnooze);
      if (now >= dueAt) {
        return { prod, payment: p };
      }
    }
  }
  return null;
}

export function PaymentReminders({ companyId, productions: externalProductions }) {
  // We accept productions from props OR fetch them ourselves on a
  // longer interval. When the host page passes productions, we use
  // those (instant reactivity to user edits). Otherwise we poll the
  // server every 90s.
  const [fetched, setFetched] = useState([]);
  const productions = externalProductions ?? fetched;
  // Soft snoozes for THIS tab only — server persists snooze_until too.
  const [snoozeMap, setSnoozeMap] = useState({});
  // Tick to force a recompute every 30s so a passing scheduled_at
  // surfaces a popup without waiting for a refetch.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (externalProductions) return;
    let mounted = true;
    const load = async () => {
      try {
        const data = await listProductions(companyId);
        if (mounted) setFetched(data || []);
      } catch { /* swallow — keep showing whatever we last had */ }
    };
    load();
    const id = setInterval(load, 90 * 1000);
    return () => { mounted = false; clearInterval(id); };
  }, [companyId, externalProductions]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  const due = useMemo(() => pickDueReminder(productions, snoozeMap), [productions, snoozeMap, tick]);
  const open = !!due;

  const onSnooze = async (hours) => {
    if (!due) return;
    const until = new Date(Date.now() + hours * HOUR).toISOString();
    setSnoozeMap((m) => ({ ...m, [due.payment.id]: Date.now() + hours * HOUR }));
    try {
      await updatePayment(due.prod.id, due.payment.id, { snooze_until: until }, companyId);
    } catch { /* the client-side snooze covers us if the call fails */ }
  };

  const onGoCollect = () => {
    if (!due) return;
    // Mark as snoozed for an hour so we don't immediately re-pop while
    // the user is taking the action, then navigate.
    onSnooze(1);
    // The production page lives at /:slug/full-frame/:prodId in some
    // routings; safer to compute the prod URL with the current path's
    // slug prefix preserved.
    const slug = window.location.pathname.split("/")[1] || "full-frame";
    window.location.href = `/${slug}/production/${due.prod.id}#ff-payments-section`;
  };

  if (!open) return null;

  const businessName = due.prod.client_name || due.prod.name || "הלקוח";
  const amount = Number(due.payment.amount) || 0;

  return (
    <Dialog open={open} onOpenChange={() => { /* not closeable without choosing */ }}>
      <DialogContent dir="rtl" className="bg-stone-900 border-amber-500/40 text-stone-100 sm:max-w-md" data-testid="ff-payment-reminder">
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mb-2">
            <Bell className="h-7 w-7 text-amber-300" />
          </div>
          <DialogTitle className="text-center text-amber-200">תזכורת לגביית תשלום</DialogTitle>
          <DialogDescription className="text-center text-stone-300 leading-relaxed">
            יש לך לגבות תשלום מ-<strong className="text-white">{businessName}</strong>
            <br />
            על סך <strong className="text-emerald-300">{ils0(amount)}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 text-center text-[11px] text-stone-400">
          הפקה: <span className="font-bold text-stone-200">{due.prod.name}</span>
          {due.payment.scheduled_at && (
            <> · נקבע ל-{new Date(due.payment.scheduled_at).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 mt-3">
          <Button
            onClick={onGoCollect}
            className="bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:opacity-90 text-white font-extrabold w-full"
            data-testid="ff-reminder-collect"
          >
            <ArrowRight className="h-4 w-4 ml-2" />
            מעבר לגביה
          </Button>
          <Button
            onClick={() => onSnooze(4)}
            variant="outline"
            className="border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-100 font-bold"
            data-testid="ff-reminder-later"
          >
            <Clock className="h-4 w-4 ml-2" />
            הזכר לי מאוחר יותר (בעוד 4 שעות)
          </Button>
          <Button
            onClick={() => onSnooze(24)}
            variant="outline"
            className="border-white/15 bg-stone-800 hover:bg-stone-700 text-stone-100 font-bold"
            data-testid="ff-reminder-tomorrow"
          >
            <AlarmClock className="h-4 w-4 ml-2" />
            הזכר לי מחר
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PaymentReminders;
